import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';

function BarraProgresso({ percentual }) {
  const cor =
    percentual >= 80
      ? 'bg-green-500'
      : percentual >= 40
      ? 'bg-brand-600'
      : 'bg-amber-500';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all duration-500 ${cor}`}
          style={{ width: `${Math.min(percentual, 100)}%` }}
        />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-8 text-right">
        {percentual}%
      </span>
    </div>
  );
}

function SkeletonLinha() {
  return (
    <tr className="animate-pulse">
      {[...Array(7)].map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-3 bg-gray-100 rounded w-full" />
        </td>
      ))}
    </tr>
  );
}

function formatarCNPJ(cnpj) {
  if (!cnpj) return '—';
  const s = cnpj.replace(/\D/g, '');
  if (s.length !== 14) return cnpj;
  return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function formatarData(valor) {
  if (!valor) return '—';
  const d = new Date(valor);
  if (isNaN(d)) return valor;
  return d.toLocaleDateString('pt-BR');
}

function formatarDataHora(valor) {
  if (!valor) return '—';
  const d = new Date(valor);
  if (isNaN(d)) return valor;
  return d.toLocaleString('pt-BR');
}

const FILTROS = [
  { valor: 'todos', label: 'Todos' },
  { valor: 'andamento', label: 'Em andamento' },
  { valor: 'finalizados', label: 'Finalizados' },
];

export default function Dashboard() {
  const [onboardings, setOnboardings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const nav = useNavigate();

  useEffect(() => {
    carregarOnboardings();
  }, []);

  async function carregarOnboardings() {
    setLoading(true);
    setErro('');
    try {
      const { data } = await api.get('/admin/onboardings');
      setOnboardings(data);
    } catch (err) {
      setErro('Erro ao carregar onboardings. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem('ht_token');
    localStorage.removeItem('ht_usuario');
    nav('/login');
  }

  const lista = onboardings.filter(o => {
    if (filtro === 'andamento') return !o.finalizado;
    if (filtro === 'finalizados') return !!o.finalizado;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="flex items-center justify-between px-6 py-3 gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo_ht.png" alt="HeadThink" className="h-9 w-auto" />
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <p className="text-xs text-gray-400 leading-none">HeadThink Soluções</p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                Painel Administrativo!
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/modulos" className="btn-ghost text-xs">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Módulos
            </Link>
            <Link to="/admin/novo" className="btn-primary text-xs">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Onboarding
            </Link>
            <button onClick={handleLogout} className="btn-ghost text-xs">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Título + filtros */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="section-title">Onboardings</h1>
            <p className="section-sub mb-0">
              {loading ? 'Carregando...' : `${lista.length} registro${lista.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg">
            {FILTROS.map(f => (
              <button
                key={f.valor}
                onClick={() => setFiltro(f.valor)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtro === f.valor
                    ? 'bg-white text-brand-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {erro && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{erro}</p>
          </div>
        )}

        {/* Tabela */}
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Empresa
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    CNPJ
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Data Entrada
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Progresso
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Último Acesso
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  [...Array(5)].map((_, i) => <SkeletonLinha key={i} />)
                ) : lista.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <p className="font-medium text-gray-500">Nenhum onboarding encontrado</p>
                          <p className="text-xs mt-1">
                            {filtro !== 'todos'
                              ? 'Tente mudar o filtro acima.'
                              : 'Clique em "Novo Onboarding" para começar.'}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  lista.map(o => (
                    <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{o.nomeFantasia}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{o.razaoSocial}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                        {formatarCNPJ(o.cnpj)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {formatarData(o.dataEntrada)}
                      </td>
                      <td className="px-4 py-3">
                        <BarraProgresso percentual={o.percentual ?? 0} />
                      </td>
                      <td className="px-4 py-3">
                        {o.finalizado ? (
                          <span className="badge-ok">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                            Finalizado
                          </span>
                        ) : (
                          <span className="badge-pending">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                            Em andamento
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {formatarDataHora(o.ultimoAcesso)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            to={`/admin/onboarding/${o.id}`}
                            className="btn-ghost text-xs px-3 py-1.5"
                          >
                            Editar
                          </Link>
                          <Link
                            to={`/admin/onboarding/${o.id}/historico`}
                            className="btn-ghost text-xs px-3 py-1.5"
                          >
                            Histórico
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
